import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useCmsContent } from '@/hooks/useCmsContent';
import PageSEO from '@/components/PageSEO';
import { 
  Rocket, 
  Globe, 
  Calculator, 
  FileText, 
  Users,
  Building2,
  Plane,
  Receipt,
  ArrowRight,
  CheckCircle,
  Cpu
} from 'lucide-react';

const ServiziPage = () => {
  const { t: cms } = useCmsContent('servizi');
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const tx = (cmsKey, i18nKey) => lang === 'it' ? (cms(cmsKey) || t(i18nKey)) : t(i18nKey);
  const services = [
    {
      id: 'innovazione-digitale',
      icon: Cpu,
      title: lang === 'en' ? 'Digital Innovation' : 'Innovazione Digitale',
      description: lang === 'en' 
        ? 'Support for startups, SMEs and professionals in developing custom software and digital applications with AI technologies.'
        : 'Affiancamento a startup, PMI e professionisti nello sviluppo di software e applicativi digitali personalizzati con tecnologie AI.',
      features: lang === 'en' ? [
        'Needs analysis and goal definition',
        'Functional and economic design',
        'Development and implementation of solutions',
        'Process automation and data analysis',
        'Results monitoring and optimization'
      ] : [
        'Analisi esigenze e definizione obiettivi',
        'Progettazione funzionale ed economica',
        'Sviluppo e implementazione soluzioni',
        'Automazione processi e analisi dati',
        'Monitoraggio risultati e ottimizzazione'
      ],
      color: 'teal',
      featured: true
    },
    {
      id: 'startup',
      icon: Rocket,
      title: lang === 'en' ? 'Startup Consulting' : 'Consulenza per Startup',
      description: lang === 'en' 
        ? 'Complete assistance for launching and managing startups and innovative new businesses.'
        : 'Assistenza completa per l\'avvio e la gestione di startup e nuovi business innovativi.',
      features: lang === 'en' ? [
        'General framework for new activities',
        'Choice of legal form',
        'Preferential tax regimes',
        'Tax and social security obligations',
        'Business plan and strategic planning'
      ] : [
        'Inquadramento generale delle nuove attività',
        'Scelta della forma giuridica',
        'Regimi fiscali agevolati',
        'Adempimenti fiscali e previdenziali',
        'Business plan e pianificazione strategica'
      ],
      color: 'indigo'
    },
    {
      id: 'ecommerce',
      icon: Globe,
      title: lang === 'en' ? 'E-commerce & Web Business' : 'E-commerce & Web Business',
      description: lang === 'en'
        ? 'Specialized tax support for online sales and digital activities.'
        : 'Supporto fiscale specializzato per vendite online e attività digitali.',
      features: lang === 'en' ? [
        'Tax aspects of online sales',
        'Digital operations invoicing',
        'VAT in international operations',
        'Marketplace and dropshipping',
        'International supplier management'
      ] : [
        'Aspetti fiscali delle vendite online',
        'Fatturazione operazioni digitali',
        'IVA nelle operazioni con l\'estero',
        'Marketplace e dropshipping',
        'Gestione fornitori internazionali'
      ],
      color: 'emerald'
    },
    {
      id: 'international',
      icon: Plane,
      title: lang === 'en' ? 'International Taxation' : 'Fiscalità Internazionale',
      description: lang === 'en'
        ? 'Management of foreign income and consulting on double taxation conventions.'
        : 'Gestione di redditi esteri e consulenza sulle convenzioni contro le doppie imposizioni.',
      features: lang === 'en' ? [
        'Transfer abroad and AIRE registration',
        'Double taxation conventions',
        'Income produced abroad',
        'Tax residence and foreign entities',
        'Receipt abroad of Italian income'
      ] : [
        'Trasferimento all\'estero e iscrizione AIRE',
        'Convenzioni contro le doppie imposizioni',
        'Redditi prodotti all\'estero',
        'Residenza fiscale ed esterovestizione',
        'Percezione all\'estero di redditi italiani'
      ],
      color: 'amber'
    },
    {
      id: 'contabilita',
      icon: Calculator,
      title: lang === 'en' ? 'Accounting & Financial Statements' : 'Contabilità & Bilanci',
      description: lang === 'en'
        ? 'Ordinary and simplified bookkeeping with all related obligations.'
        : 'Tenuta della contabilità ordinaria e semplificata con tutti gli adempimenti correlati.',
      features: lang === 'en' ? [
        'Ordinary and simplified accounting',
        'Periodic VAT settlements',
        'F24 form preparation',
        'Annual financial statements',
        'Filing with Chamber of Commerce'
      ] : [
        'Contabilità ordinaria e semplificata',
        'Liquidazioni IVA periodiche',
        'Predisposizione modelli F24',
        'Bilancio annuale e relazioni',
        'Deposito presso CCIAA'
      ],
      color: 'violet'
    },
    {
      id: 'fatturazione',
      icon: Receipt,
      title: lang === 'en' ? 'Electronic Invoicing' : 'Fatturazione Elettronica',
      description: lang === 'en'
        ? 'Complete management of active and passive cycle with the Exchange System (SDI).'
        : 'Gestione completa del ciclo attivo e passivo con il Sistema di Interscambio (SDI).',
      features: lang === 'en' ? [
        'SDI interconnection',
        'Active and passive cycle management',
        'Digital preservation',
        'Continuous technical support',
        'Personalized training'
      ] : [
        'Interconnessione con SDI',
        'Gestione ciclo attivo e passivo',
        'Conservazione digitale',
        'Assistenza tecnica continua',
        'Formazione personalizzata'
      ],
      color: 'rose'
    },
    {
      id: 'dichiarazioni',
      icon: FileText,
      title: lang === 'en' ? 'Tax Returns' : 'Dichiarazioni Fiscali',
      description: lang === 'en'
        ? 'Preparation and electronic submission of all mandatory tax returns.'
        : 'Redazione e invio telematico di tutte le dichiarazioni fiscali obbligatorie.',
      features: lang === 'en' ? [
        'Income tax returns',
        'Intrastat models',
        'Periodic communications',
        'Advance and balance calculations',
        'Assessment assistance'
      ] : [
        'Dichiarazioni dei redditi',
        'Modelli Intrastat',
        'Comunicazioni periodiche',
        'Calcolo acconti e saldi',
        'Assistenza in caso di accertamento'
      ],
      color: 'cyan'
    }
  ];

  const targetClients = [
    { icon: Rocket, label: 'Startup' },
    { icon: Users, label: 'Freelancer' },
    { icon: Building2, label: 'PMI' },
    { icon: Globe, label: 'E-commerce' }
  ];

  return (
    <div className="min-h-screen" data-testid="servizi-page">
      <PageSEO path="/servizi" />
      {/* Hero */}
      <section className="bg-white py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-sm font-medium tracking-widest uppercase text-indigo-600 mb-4 block">
              I Nostri Servizi
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
              {t('page_title', 'Soluzioni fiscali complete per il tuo business')}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              {t('page_subtitle', "Un'offerta caratterizzata da elevato standard qualitativo, continuo aggiornamento e pricing adeguato alla realtà del cliente.")}
            </p>
          </motion.div>

          {/* Target Clients */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4 mt-12"
          >
            {targetClients.map((client, index) => (
              <div
                key={client.label}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full"
              >
                <client.icon className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-medium text-slate-700">{client.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Service - Innovazione Digitale */}
      <section className="py-16 bg-white" data-testid="featured-service">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {services.filter(s => s.featured).map((service) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-teal-600 to-cyan-700 rounded-3xl p-8 lg:p-12 text-white relative overflow-hidden"
              data-testid={`service-${service.id}`}
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
              <div className="relative z-10">
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start">
                  <div className="flex-1">
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-xs font-medium mb-6 backdrop-blur-sm">
                      <Cpu className="w-3.5 h-3.5" />
                      {lang === 'en' ? 'Featured Service' : 'Servizio in Evidenza'}
                    </span>
                    <h2 className="text-3xl lg:text-4xl font-bold mb-4">{service.title}</h2>
                    <p className="text-teal-100 text-lg leading-relaxed mb-6">{service.description}</p>
                    <Link to="/contatti">
                      <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50">
                        {lang === 'en' ? 'Request Information' : 'Richiedi Informazioni'}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  </div>
                  <div className="flex-1 w-full">
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                      <ul className="space-y-4">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-teal-200 flex-shrink-0 mt-0.5" />
                            <span className="text-white/90">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.filter(s => !s.featured).map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
                className="bg-white rounded-2xl p-8 border border-slate-200 hover:border-indigo-200 hover:shadow-xl transition-all"
                data-testid={`service-${service.id}`}
              >
                <div className={`w-14 h-14 bg-${service.color}-100 rounded-xl flex items-center justify-center mb-6`}>
                  <service.icon className={`w-7 h-7 text-${service.color}-600`} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 mb-6">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Philosophy */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-sm font-medium tracking-widest uppercase text-indigo-600 mb-4 block">
                La Nostra Filosofia
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {t('pricing_title', 'Pricing trasparente e personalizzato')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                {t('pricing_description', "Crediamo in un approccio al pricing che tenga conto della realtà dimensionale di ogni cliente. Nessun costo nascosto, solo trasparenza e valore.")}
              </p>
              <ul className="space-y-4">
                {[
                  'Preventivi chiari e dettagliati',
                  'Pacchetti personalizzabili',
                  'Nessun costo nascosto',
                  'Fatturazione mensile o annuale'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-indigo-600" />
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1714974528757-f63c72154a1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxmaW5hbmNpYWwlMjBhZHZpc29yJTIwbWVldGluZyUyMGNsaWVudCUyMGNvdXBsZSUyMG9mZmljZSUyMGNvbnN1bHRhdGlvbnxlbnwwfHx8fDE3NzMwODU0MTh8MA&ixlib=rb-4.1.0&q=85&w=800"
                alt="Consulenza professionale"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Hai bisogno di un servizio specifico?
            </h2>
            <p className="text-lg text-indigo-100 mb-8">
              Contattaci per discutere delle tue esigenze e ricevere un preventivo personalizzato.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/consulenza">
                <Button size="lg" className="bg-white text-indigo-600 hover:bg-slate-100 w-full sm:w-auto" data-testid="cta-consultation">
                  Prenota Consulenza
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contatti">
                <Button size="lg" variant="outline" className="text-white border-white/30 hover:bg-white/10 w-full sm:w-auto">
                  Contattaci
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiziPage;
